import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { isClickOutside } from "../../utils/clickOutside";
import { useWidgetI18n } from "../../context/WidgetI18nContext";
import { useDialogFocus } from "../../hooks/useDialogFocus";
import { isToolAvailable } from "@vninvestcharts/engine-drawing";
import { ToolGlyph, IconCode, IconFlask } from "../common/Icons";
import styles from "../../styles/components/MobileToolbar.module.css";
// P2-a: icon glyphs come from the central registry via <ToolGlyph/> (no emoji data kept here).
const DRAWING_TOOLS = [
    "cursor", "crosshair", "trendLine", "ray", "extendedLine", "parallelChannel",
    "hLine", "vLine", "priceLabel", "fibonacci", "fibExtension", "fibChannel",
    "fibTimeZone", "pitchfork", "schiffPitchfork", "gannBox", "gannFan", "brush",
    "rectangle", "ellipse", "triangle", "arc", "text", "callout", "arrow",
    "dateAndPriceRange", "dateRange", "priceRange", "eraser",
].map((id) => ({ id }));
const DEFAULT_TIMEFRAMES = ["1m", "5m", "15m", "1h", "4h", "1D", "1W", "1M"];
export function MobileToolbar({ isOpen, onClose, activeTool, onToolSelect, timeframes = DEFAULT_TIMEFRAMES, activeTimeframe, onTimeframeChange, portalRoot, activeTab = null, onTabChange, isChartFocused = false, charts = [], activeChartId = null, onChartSelect, patTier, onPremiumToolClick, }) {
    const { t } = useWidgetI18n();
    const [dragY, setDragY] = useState(0);
    const startYRef = useRef(0);
    const isDraggingRef = useRef(false);
    const sheetRef = useRef(null);
    // Reset drag position on open
    useEffect(() => {
        if (isOpen) {
            setDragY(0);
        }
    }, [isOpen]);
    // Click outside / click overlay to close
    useEffect(() => {
        if (!isOpen)
            return;
        const handleClick = (e) => {
            if (isClickOutside(sheetRef.current, e)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [isOpen, onClose]);
    // UX-02: the mobile bottom sheet is a dialog — give it the shared focus
    // contract (initial focus, Tab loop, Escape to dismiss, focus restore).
    //
    // MUST be called before the `if (!isOpen) return null` below. It used to sit
    // after that early return, so the hook count differed between the closed and
    // the open render: React threw "Rendered more hooks than during the previous
    // render", the error boundary caught it, and tapping the FAB replaced the
    // whole workspace with the error card. `open: isOpen` (instead of a
    // hardcoded `true`) also keeps the trap inert while the sheet is closed.
    useDialogFocus(sheetRef, { open: isOpen, onEscape: onClose });
    if (!isOpen)
        return null;
    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        startYRef.current = touch.clientY;
        isDraggingRef.current = true;
    };
    const handleTouchMove = (e) => {
        if (!isDraggingRef.current)
            return;
        const touch = e.touches[0];
        const deltaY = touch.clientY - startYRef.current;
        // Only allow dragging downwards
        if (deltaY > 0) {
            setDragY(deltaY);
        }
    };
    const handleTouchEnd = () => {
        if (!isDraggingRef.current)
            return;
        isDraggingRef.current = false;
        if (dragY > 100) {
            onClose();
        }
        else {
            setDragY(0);
        }
    };
    const content = (_jsx("div", { className: styles.overlay, children: _jsxs("div", { ref: sheetRef, className: styles.bottomSheet, style: { transform: `translateY(${dragY}px)` }, role: "dialog", "aria-modal": "true", "aria-label": t("mobile.settings") ?? "Chart tools", children: [_jsx("div", { className: styles.handleBarContainer, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, children: _jsx("div", { className: styles.handleBar }) }), _jsxs("div", { className: styles.content, children: [charts && charts.length > 1 && onChartSelect && (_jsxs("div", { className: styles.section, children: [_jsx("div", { className: styles.sectionTitle, children: t("mobile.selectChart") }), _jsx("div", { className: styles.chartsGridCompact, children: charts.map((chart) => {
                                        const isActive = chart.id === activeChartId;
                                        return (_jsxs("button", { type: "button", className: `${styles.chartItemCompact} ${isActive ? styles.active : ""}`, onClick: () => {
                                                onChartSelect(chart.id);
                                                onClose();
                                            }, children: [_jsx("span", { className: styles.chartIndicator, style: {
                                                        width: "6px",
                                                        height: "6px",
                                                        borderRadius: "50%",
                                                        backgroundColor: isActive ? "var(--vnsc-accent, #22d3ee)" : "rgba(255, 255, 255, 0.4)",
                                                        display: "inline-block",
                                                        marginRight: "6px"
                                                    } }), _jsx("span", { className: styles.chartSymbol, children: chart.symbol || "—" }), _jsx("span", { className: styles.chartTimeframe, children: chart.timeframe })] }, chart.id));
                                    }) })] })), onTimeframeChange && timeframes && (_jsxs("div", { className: styles.section, children: [_jsx("div", { className: styles.sectionTitle, children: t("mobile.timeframe") }), _jsx("div", { className: styles.timeframeGrid, children: timeframes.map(tf => (_jsx("button", { type: "button", className: `${styles.timeframeButton} ${tf === activeTimeframe ? styles.active : ""}`, onClick: () => {
                                            onTimeframeChange(tf);
                                            onClose();
                                        }, children: tf }, tf))) })] })), _jsxs("div", { className: styles.section, children: [_jsx("div", { className: styles.sectionTitle, children: t("mobile.drawingTools") }), _jsx("div", { className: styles.toolsGrid, children: DRAWING_TOOLS.map(tool => {
                                        const available = isToolAvailable(tool.id, patTier ?? "free"); // ARC-1-keep: legacy bridge
                                        return (_jsxs("button", { type: "button", className: `${styles.toolButton} ${tool.id === activeTool ? styles.active : ""} ${!available ? styles.disabled : ""}`, onClick: () => {
                                                if (!available) {
                                                    onPremiumToolClick?.(tool.id);
                                                    return;
                                                }
                                                onToolSelect(tool.id);
                                                onClose();
                                            }, title: !available ? t("tool.premiumLocked") : undefined, style: { opacity: available ? undefined : 0.35, cursor: available ? undefined : "not-allowed" }, children: [_jsx("span", { className: styles.toolIcon, children: _jsx(ToolGlyph, { id: tool.id }) }), _jsx("span", { className: styles.toolLabel, children: t(`drawingTool.${tool.id}`) })] }, tool.id));
                                    }) })] }), onTabChange && (_jsxs("div", { className: styles.section, children: [_jsx("div", { className: styles.sectionTitle, children: t("mobile.features") }), _jsxs("div", { className: styles.featuresGrid, children: [_jsxs("button", { type: "button", className: `${styles.featureButton} ${activeTab === "pine_editor" ? styles.active : ""}`, onClick: () => {
                                                onTabChange(activeTab === "pine_editor" ? null : "pine_editor");
                                                onClose();
                                            }, children: [_jsx(IconCode, { size: 14 }), " ", t("mobile.pineEditor")] }), _jsxs("button", { type: "button", className: `${styles.featureButton} ${activeTab === "strategy_tester" ? styles.active : ""}`, onClick: () => {
                                                onTabChange(activeTab === "strategy_tester" ? null : "strategy_tester");
                                                onClose();
                                            }, children: [_jsx(IconFlask, { size: 14 }), " ", t("mobile.strategyTester")] })] })] }))] })] }) }));
    // Render into the portalRoot if provided, otherwise fallback to document.body
    const mountTarget = portalRoot || (typeof document !== "undefined" ? document.body : null);
    if (!mountTarget)
        return null;
    return createPortal(content, mountTarget);
}
